/*
 * <store-locator> — embeds a Google Maps iframe whose query updates from
 * the selected retailer chip + ZIP/geolocation. The first chip is "All
 * retailers" (selects every block); the rest are single retailers.
 *
 * Uses the official Embed API when a key is configured, falls back to
 * the keyless legacy embed otherwise.
 */
(() => {
  if (customElements.get('store-locator')) return;

  const DEBOUNCE_MS = 350;
  const EMBED_API = 'https://www.google.com/maps/embed/v1/search';
  const LEGACY_EMBED = 'https://www.google.com/maps?output=embed';
  const US_CENTER = '39.5,-98.35';
  const US_ZOOM = 4;

  // Send the event directly to GA4 via gtag (Littledata doesn't gate
  // custom events — they enrich ecommerce events and forward to GA4 via
  // the same gtag pipeline) and also push to the raw dataLayer for any
  // GTM-driven listeners. Both are no-ops if their globals aren't there,
  // so this is safe to call anywhere.
  //
  // Gated by Shopify Customer Privacy (CIPA): if the visitor has not
  // granted analytics consent, the event is dropped on the floor — we
  // do not push to dataLayer either, because GTM listeners can forward
  // dataLayer events to third parties.
  function track(event, payload) {
    if (typeof window === 'undefined') return;
    const cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp && typeof cp.userCanBeTracked === 'function' && !cp.userCanBeTracked()) return;
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', event, payload);
      }
    } catch (_) {}
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...payload });
  }

  function buildSrc({ apiKey, query, zoom, hasLocation }) {
    const effectiveZoom = hasLocation ? zoom : US_ZOOM;
    if (apiKey) {
      const params = new URLSearchParams({
        key: apiKey,
        q: query,
        zoom: String(effectiveZoom),
      });
      if (!hasLocation) params.set('center', US_CENTER);
      return `${EMBED_API}?${params}`;
    }
    const params = new URLSearchParams({ q: query, z: String(effectiveZoom) });
    if (!hasLocation) params.set('ll', US_CENTER);
    return `${LEGACY_EMBED}&${params}`;
  }

  class StoreLocator extends HTMLElement {
    connectedCallback() {
      this.zipInput = this.querySelector('.js-zip');
      this.geoBtn = this.querySelector('.js-geo');
      this.iframe = this.querySelector('.js-iframe');
      this.presets = this.querySelectorAll('.js-preset');
      this.defaultQuery = this.dataset.defaultQuery || 'Walmart OR CVS OR Target OR Walgreens';
      this.apiKey = this.dataset.apiKey || '';
      this.zoom = Number(this.dataset.zoom) || 12;
      this.location = '';
      this.selectedChain = ''; // empty = "All retailers"
      this.timer = null;

      this.update('init');
      this.bind();
    }

    bind() {
      this.zipInput.addEventListener('input', () => {
        const v = this.zipInput.value.trim();
        if (v && !/^\d{5}$/.test(v)) return;
        this.location = v;
        this.scheduleUpdate('typed_zip');
      });
      this.geoBtn.addEventListener('click', () => this.useGeolocation());
      this.presets.forEach((btn) => {
        btn.addEventListener('click', () => this.selectChain(btn.dataset.chain || ''));
      });
    }

    selectChain(chain) {
      if (this.selectedChain === chain) return;
      this.selectedChain = chain;
      this.update('preset');
      track('store_locator_preset_click', { chain: chain || 'all' });
    }

    scheduleUpdate(source) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.update(source), DEBOUNCE_MS);
    }

    refreshActivePreset() {
      this.presets.forEach((btn) => {
        const isActive = (btn.dataset.chain || '') === this.selectedChain;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });
    }

    currentQuery() {
      const retailer = this.selectedChain || this.defaultQuery;
      return this.location ? `${retailer} near ${this.location}` : retailer;
    }

    update(source) {
      const query = this.currentQuery();
      this.iframe.src = buildSrc({
        apiKey: this.apiKey,
        query,
        zoom: this.zoom,
        hasLocation: !!this.location,
      });
      this.refreshActivePreset();
      if (source !== 'init') {
        track('store_locator_search', {
          retailer: this.selectedChain || 'all',
          location: this.location || null,
          source,
        });
      }
    }

    useGeolocation() {
      if (!('geolocation' in navigator)) {
        track('store_locator_geolocate', { outcome: 'unsupported' });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude.toFixed(5);
          const lon = pos.coords.longitude.toFixed(5);
          this.location = `${lat},${lon}`;
          this.zipInput.value = '';
          this.update('geolocate');
          track('store_locator_geolocate', { outcome: 'granted' });
        },
        () => {
          track('store_locator_geolocate', { outcome: 'denied' });
        },
        { timeout: 8000, maximumAge: 60_000 },
      );
    }
  }

  customElements.define('store-locator', StoreLocator);
})();
